import type { BodyJson, SeoJson } from "../../../types/cms";

const TITLE_MAX = 255;
const ALLOWED_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/svg+xml",
];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export function validateCmsImageFile(file: File): string | null {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return "Only PNG, JPEG, WebP, and SVG images are allowed.";
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return "Image must be 5 MB or smaller.";
  }
  return null;
}

export function validatePageTitle(title: string): string | null {
  const t = title?.trim() ?? "";
  if (!t) {
    return "Title is required.";
  }
  if (t.length > TITLE_MAX) {
    return `Title must be at most ${TITLE_MAX} characters.`;
  }
  return null;
}

export function validateSeo(seo: SeoJson): string | null {
  const meta = seo?.title?.trim() ?? "";
  if (!meta) {
    return "SEO meta title is required.";
  }
  return null;
}

export function validateBodyForSave(body: BodyJson): string | null {
  if (!body || typeof body !== "object") {
    return "Invalid page content.";
  }
  if (!Array.isArray(body.sections)) {
    return "Sections must be an array.";
  }
  const keys = new Set<string>();
  for (let i = 0; i < body.sections.length; i++) {
    const s = body.sections[i];
    if (!s?.type || typeof s.type !== "string") {
      return `Section ${i + 1}: type is required.`;
    }
    if (!s.props || typeof s.props !== "object") {
      return `Section ${i + 1}: props must be an object.`;
    }
    if (s.key) {
      if (keys.has(s.key)) {
        return `Duplicate section key: ${s.key}`;
      }
      keys.add(s.key);
    }
    if (s.type === "hero") {
      const img = s.props.heroImage as { fileKey?: string; alt?: string } | null;
      if (img && img.fileKey && !(img.alt || "").trim()) {
        return "Hero image requires alt text.";
      }
    }
  }
  return null;
}
